import { useEffect, useState, useMemo } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Trophy, Star, Zap, ChevronRight, Home, ArrowRight, RotateCcw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useConfetti } from '../../../hooks/cabin/useConfetti';
import { useSoundEffects } from '../../../hooks/cabin/useSoundEffects';
import { useCabinModule } from '../../../hooks/cabin/useCabinModule';
import PointsAnimation from '../PointsAnimation';
import BadgeUnlockModal from '../BadgeUnlockModal';

/**
 * CompletePhase
 *
 * Props:
 *   result      — backend complete response
 *                 { total_score, max_score, score_percent, xp_earned, miles_earned,
 *                   is_first_pass, badges_unlocked }
 *   lesson      — lesson detail object (unit_id, lesson_id, title, …)
 *   moduleSlug  — slug of the current module (passed from CabinLessonPlayer via
 *                 location.state set in CabinModuleDetail)
 *   onContinue  — optional callback (kept for backward compat, unused by default)
 */
export default function CompletePhase({
  result = {},
  lesson = {},
  moduleSlug = null,
  onContinue,
  'data-testid': testId = 'complete-phase',
}) {
  const { t, lang } = useLanguage();
  const { fire } = useConfetti();
  const { play } = useSoundEffects();
  const navigate = useNavigate();
  const shouldReduce = useReducedMotion();

  // ── Reward values ────────────────────────────────────────────────────────
  const xpEarned = result?.xp_earned || lesson?.xp_reward || 0;
  const milesEarned = result?.miles_earned || lesson?.miles_reward || 0;
  const badgesUnlocked = result?.badges_unlocked || [];

  // ── Score: prefer backend score_percent; fallback to frontend ratio ──────
  // Backend: score_percent is computed from accumulated submit-phase totals.
  // If backend returns 0 but we have total_score/max_score, recalc.
  const rawScorePct = result?.score_percent ?? null;
  const totalScore = result?.total_score ?? 0;
  const maxScore = result?.max_score ?? 0;
  const scorePct =
    rawScorePct != null && rawScorePct > 0
      ? Math.round(rawScorePct)
      : maxScore > 0
      ? Math.round((totalScore / maxScore) * 100)
      : 0;

  // ── Pass / fail (failed lessons aren't completed → show retry) ────────────
  const passThreshold = result?.pass_threshold ?? 50;
  const isPassed = result?.is_passed ?? (maxScore > 0 ? scorePct >= passThreshold : true);

  // ── Badge modal ──────────────────────────────────────────────────────────
  const [badgeModalOpen, setBadgeModalOpen] = useState(false);

  // ── Module data for "next lesson" lookup ────────────────────────────────
  const { data: moduleData } = useCabinModule(moduleSlug);

  /**
   * Find next lesson: flatten all lessons from module units, find current
   * lesson's position, return the next one.
   */
  const nextLesson = useMemo(() => {
    if (!moduleData || !lesson?.lesson_id) return null;
    const allLessons = (moduleData.units || []).flatMap((u) => u.lessons || []);
    const idx = allLessons.findIndex(
      (l) => (l.lesson_id || l.code) === lesson.lesson_id
    );
    if (idx === -1 || idx >= allLessons.length - 1) return null;
    const candidate = allLessons[idx + 1];
    // Don't navigate to a locked lesson
    if (candidate?.status === 'locked') return null;
    return candidate;
  }, [moduleData, lesson?.lesson_id]);

  const isLastLesson = moduleSlug && moduleData && !nextLesson;

  // ── Effects ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (isPassed) {
      if (!shouldReduce) {
        fire({ particleCount: 120, spread: 80, origin: { y: 0.6 } });
      }
      play('success');
      if (badgesUnlocked.length > 0) {
        const timer = setTimeout(() => setBadgeModalOpen(true), 1200);
        return () => clearTimeout(timer);
      }
    } else {
      play('wrong');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRetry = () => {
    // navigate(0) triggers a soft reload via React Router, re-mounting the
    // player without a full page reload (avoids losing auth state / cache).
    navigate(0);
  };

  const lessonTitle =
    lang === 'tr'
      ? (lesson?.title?.tr || lesson?.title_tr || (typeof lesson?.title === 'string' ? lesson.title : '') || '')
      : (lesson?.title?.en || lesson?.title_en || lesson?.title?.tr || (typeof lesson?.title === 'string' ? lesson.title : '') || '');

  // ── Navigation ───────────────────────────────────────────────────────────
  const handleNextLesson = () => {
    if (!nextLesson) return;
    const nextId = nextLesson.lesson_id || nextLesson.code;
    navigate(`/programs/kabin-memurlugu/lessons/${nextId}`, {
      state: { moduleSlug },
    });
  };

  const handleBackToModule = () => {
    if (moduleSlug) {
      navigate(`/programs/kabin-memurlugu/modules/${moduleSlug}`);
    } else {
      navigate('/programs/kabin-memurlugu/modules');
    }
  };

  return (
    <>
      {badgeModalOpen && badgesUnlocked.length > 0 && (
        <BadgeUnlockModal
          badges={badgesUnlocked}
          onClose={() => setBadgeModalOpen(false)}
        />
      )}
      <motion.div
        initial={shouldReduce ? {} : { opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: 'spring', damping: 18, stiffness: 200 }}
        className="flex flex-col items-center gap-8 w-full max-w-lg mx-auto py-8 text-center"
        data-testid={testId}
      >
        {/* ── Trophy icon ─────────────────────────────────────────────── */}
        <motion.div
          initial={shouldReduce ? {} : { scale: 0, rotate: -15 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', damping: 10, stiffness: 200, delay: 0.1 }}
          className={`w-20 h-20 flex items-center justify-center rounded-3xl ${
            isPassed
              ? 'bg-gradient-to-br from-amber-400 to-yellow-500 shadow-[0_8px_0_0_#d97706]'
              : 'bg-gradient-to-br from-slate-300 to-slate-400 shadow-[0_8px_0_0_#94a3b8]'
          }`}
          aria-hidden="true"
        >
          {isPassed ? (
            <Trophy size={40} className="text-white" />
          ) : (
            <RotateCcw size={38} className="text-white" />
          )}
        </motion.div>

        {/* ── Title ───────────────────────────────────────────────────── */}
        <div>
          <h2
            className="text-2xl font-black text-[var(--text-main)] mb-1"
            data-testid={`${testId}-title`}
          >
            {isPassed
              ? (t('complete.title') || 'Ders Tamamlandı!')
              : (t('complete.failTitle') || 'Bu sefer olmadı')}
          </h2>
          {!isPassed && (
            <p className="text-sm text-[var(--text-muted)]">
              {(t('complete.failDesc') || 'Geçmek için en az %{{threshold}} gerekiyor. Tekrar dene!').replace('{{threshold}}', passThreshold)}
            </p>
          )}
          {isPassed && lessonTitle && (
            <p className="text-sm text-[var(--text-muted)]">{lessonTitle}</p>
          )}
        </div>

        {/* ── Score card ──────────────────────────────────────────────── */}
        {maxScore > 0 && (
          <div
            className="w-full p-4 bg-gray-50 border-2 border-[var(--border-light)] rounded-2xl"
            data-testid={`${testId}-score`}
          >
            <p className="text-sm text-[var(--text-muted)] mb-1">
              {t('complete.score_label') || 'Başarı Puanı'}
            </p>
            <PointsAnimation
              value={scorePct}
              suffix="%"
              className="text-3xl font-black text-[var(--brand-primary)]"
              duration={800}
              data-testid={`${testId}-score-value`}
            />
          </div>
        )}

        {/* ── Rewards ─────────────────────────────────────────────────── */}
        <div className="flex gap-4 w-full">
          {/* XP */}
          <motion.div
            initial={shouldReduce ? {} : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex-1 flex flex-col items-center gap-2 p-4 bg-amber-50 border-2 border-amber-200 rounded-2xl"
            data-testid={`${testId}-xp`}
          >
            <Zap size={24} className="text-amber-500" aria-hidden="true" />
            <PointsAnimation
              value={xpEarned}
              prefix="+"
              suffix=" XP"
              className="text-xl font-black text-amber-600"
              data-testid={`${testId}-xp-value`}
            />
            <span className="text-xs text-amber-500 font-semibold">
              {t('complete.xp_label') || 'Deneyim Puanı'}
            </span>
          </motion.div>

          {/* Miles */}
          <motion.div
            initial={shouldReduce ? {} : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            className="flex-1 flex flex-col items-center gap-2 p-4 bg-sky-50 border-2 border-sky-200 rounded-2xl"
            data-testid={`${testId}-miles`}
          >
            <Star size={24} className="text-sky-500" aria-hidden="true" />
            <PointsAnimation
              value={milesEarned}
              prefix="+"
              suffix=" Mil"
              className="text-xl font-black text-sky-600"
              data-testid={`${testId}-miles-value`}
            />
            <span className="text-xs text-sky-500 font-semibold">
              {t('complete.miles_label') || 'Havacılık Mili'}
            </span>
          </motion.div>
        </div>

        {/* ── Badges preview ──────────────────────────────────────────── */}
        {badgesUnlocked.length > 0 && (
          <div className="w-full" data-testid={`${testId}-badges`}>
            <p className="text-sm font-bold text-[var(--text-main)] mb-2">
              {t('complete.badges_unlocked') || 'Yeni Rozetler!'}
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              {badgesUnlocked.map((badge, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1.5 bg-purple-50 border border-purple-200 text-purple-700 text-xs font-bold rounded-lg"
                >
                  {badge.name_tr || badge.name || badge}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* ── Module completed banner (last lesson) ───────────────────── */}
        {isPassed && isLastLesson && (
          <motion.div
            initial={shouldReduce ? {} : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="w-full flex items-center gap-3 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-2xl"
            data-testid={`${testId}-module-complete`}
          >
            <span className="text-2xl" aria-hidden="true">🎓</span>
            <div className="text-left">
              <p className="text-sm font-black text-emerald-700">
                {t('complete.module_finished_title') || 'Modülü Bitirdiniz!'}
              </p>
              <p className="text-xs text-emerald-600">
                {t('complete.module_finished_desc') || 'Tüm dersler tamamlandı.'}
              </p>
            </div>
          </motion.div>
        )}

        {/* ── CTAs ────────────────────────────────────────────────────── */}
        <div className="flex flex-col gap-3 w-full">
          {/* Retry CTA — failed lesson */}
          {!isPassed && (
            <motion.button
              whileHover={shouldReduce ? {} : { scale: 1.01 }}
              whileTap={shouldReduce ? {} : { scale: 0.99 }}
              onClick={handleRetry}
              className="w-full flex items-center justify-center gap-2 py-3.5 bg-[var(--brand-primary)] text-white font-black text-lg rounded-xl shadow-[0_4px_0_0_var(--brand-primary-shadow)] hover:bg-[var(--brand-primary-hover)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
              aria-label={t('complete.retry') || 'Tekrar Dene'}
              data-testid={`${testId}-retry`}
            >
              <RotateCcw size={20} aria-hidden="true" />
              {t('complete.retry') || 'Tekrar Dene'}
            </motion.button>
          )}

          {/* Next lesson CTA — shown when passed and there is a next lesson */}
          {isPassed && nextLesson && (
            <motion.button
              whileHover={shouldReduce ? {} : { scale: 1.01 }}
              whileTap={shouldReduce ? {} : { scale: 0.99 }}
              onClick={handleNextLesson}
              className="w-full flex items-center justify-center gap-2 py-3.5 bg-[var(--brand-primary)] text-white font-black text-lg rounded-xl shadow-[0_4px_0_0_var(--brand-primary-shadow)] hover:bg-[var(--brand-primary-hover)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
              aria-label={t('complete.next_lesson') || 'Sonraki Derse Geç'}
              data-testid={`${testId}-next-lesson`}
            >
              {t('complete.next_lesson') || 'Sonraki Derse Geç'}
              <ArrowRight size={20} aria-hidden="true" />
            </motion.button>
          )}

          {/* onContinue callback fallback (backward compat) */}
          {isPassed && !nextLesson && onContinue && (
            <motion.button
              whileHover={shouldReduce ? {} : { scale: 1.01 }}
              whileTap={shouldReduce ? {} : { scale: 0.99 }}
              onClick={onContinue}
              className="w-full flex items-center justify-center gap-2 py-3.5 bg-[var(--brand-primary)] text-white font-black text-lg rounded-xl shadow-[0_4px_0_0_var(--brand-primary-shadow)] hover:bg-[var(--brand-primary-hover)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
              data-testid={`${testId}-continue`}
            >
              {t('complete.continue') || 'Sonraki Derse Devam Et'}
              <ChevronRight size={20} aria-hidden="true" />
            </motion.button>
          )}

          {/* Back to module button */}
          <motion.button
            whileHover={shouldReduce ? {} : { scale: 1.01 }}
            whileTap={shouldReduce ? {} : { scale: 0.99 }}
            onClick={handleBackToModule}
            className="w-full flex items-center justify-center gap-2 py-3 border-2 border-[var(--border-light)] text-[var(--text-muted)] font-bold rounded-xl hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
            aria-label={t('complete.backToModule') || 'Modüle Dön'}
            data-testid={`${testId}-back-to-module`}
          >
            <Home size={18} aria-hidden="true" />
            {t('complete.backToModule') || 'Modüle Dön'}
          </motion.button>
        </div>
      </motion.div>
    </>
  );
}
